/**
 * Image navigation for collection product cards.
 *
 * Only the featured image is in the page markup. Every other image is
 * created the first time someone uses a control, from #CardGalleryData.
 *
 * Controls are appended to .card-wrapper, never to .card__inner or .media.
 * .card__inner carries a transform, which makes it a stacking context
 * pinned below the card link's ::after — anything inside it renders but
 * cannot be tapped, whatever its z-index. Measured while building the
 * earlier flip control: hit radius 0.
 *
 * Wrapping is deliberate. Every product here has exactly two images, so
 * stopping at the ends would leave one arrow permanently disabled — dead
 * touch target on a 171px card. The dots carry position, so wrapping is
 * not disorienting.
 *
 * Dawn's `media--hover-effect` class is removed from gallery cards. It
 * drives a desktop-only hover crossfade that would fight this and would
 * make behaviour differ between mobile and desktop.
 */

(() => {
  const dataEl = document.getElementById('CardGalleryData');
  if (!dataEl) return;

  let galleryData;
  try {
    galleryData = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  if (!galleryData || !Object.keys(galleryData).length) return;

  const CHEVRON =
    '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M12.5 4 6.5 10l6 6"/></svg>';

  const SIZES = '(min-width: 990px) calc((100vw - 130px) / 4), calc((100vw - 35px) / 2)';

  const handleOf = (card) => {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    try {
      const match = new URL(link.href, window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  };

  const setUp = (card) => {
    if (card.dataset.cardGalleryReady) return;

    const media = card.querySelector('.media');
    if (!media) return;

    const featured = media.querySelector('img');
    if (!featured) return;

    const handle = handleOf(card);
    const extras = handle && galleryData[handle];
    // No entry means one image, or nothing but video after it — leave the
    // card exactly as Dawn rendered it.
    if (!extras || !extras.length) return;

    card.dataset.cardGalleryReady = 'true';
    card.classList.add('has-gallery');

    // Kill Dawn's desktop-only hover crossfade so behaviour is identical
    // everywhere, and so its opacity rules stop competing with ours.
    media.classList.remove('media--hover-effect');

    const total = extras.length + 1;
    const slots = new Array(total).fill(null);
    slots[0] = featured;
    featured.classList.add('is-active');

    let index = 0;

    const layer = document.createElement('div');
    layer.className = 'card-gallery';
    // Custom properties do not inherit upwards, so copy the ratio from the
    // image box to reproduce it on the wrapper.
    const inner = card.querySelector('.card__inner');
    const ratio = inner && getComputedStyle(inner).getPropertyValue('--ratio-percent').trim();
    if (ratio) layer.style.setProperty('--ratio-percent', ratio);

    const makeArrow = (dir, label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `card-gallery__arrow card-gallery__arrow--${dir}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = CHEVRON;
      if (dir === 'next') b.querySelector('svg').style.transform = 'rotate(180deg)';
      return b;
    };

    const prev = makeArrow('prev', 'Previous image');
    const next = makeArrow('next', 'Next image');

    const dotsWrap = document.createElement('div');
    dotsWrap.className = 'card-gallery__dots';
    const dots = [];
    for (let i = 0; i < total; i += 1) {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'card-gallery__dot';
      d.setAttribute('aria-label', `Show image ${i + 1} of ${total}`);
      d.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      dots.push(d);
      dotsWrap.appendChild(d);
    }

    layer.append(prev, next, dotsWrap);
    card.appendChild(layer);

    // Creating the <img> is what issues the network request, so nothing is
    // fetched until this runs.
    const ensure = (i) =>
      new Promise((resolve) => {
        if (slots[i]) return resolve(slots[i]);
        const data = extras[i - 1];
        if (!data) return resolve(null);

        const img = new Image();
        img.className = 'motion-reduce';
        img.alt = data.alt || '';
        img.width = data.width;
        img.height = data.height;
        img.sizes = SIZES;
        img.srcset = data.srcset;
        img.addEventListener('load', () => {
          // Appended only once decoded, so a crossfade never shows a gap.
          media.appendChild(img);
          slots[i] = img;
          resolve(img);
        }, { once: true });
        img.addEventListener('error', () => resolve(null), { once: true });
        img.src = data.src;
      });

    const show = (i) => {
      ensure(i).then((img) => {
        if (!img) return;
        slots.forEach((s) => s && s.classList.remove('is-active'));
        img.classList.add('is-active');
        index = i;
        dots.forEach((d, di) => d.setAttribute('aria-current', di === index ? 'true' : 'false'));
        // Warm the next one so a second tap is instant.
        ensure((index + 1) % total);
      });
    };

    // The card is a link; without both of these a tap would navigate.
    const bind = (el, fn) =>
      el.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        fn();
      });

    bind(prev, () => show((index - 1 + total) % total));
    bind(next, () => show((index + 1) % total));
    dots.forEach((d, di) => bind(d, () => show(di)));
  };

  const scan = () => document.querySelectorAll('.card-wrapper').forEach(setUp);
  scan();

  const grid = document.getElementById('product-grid');
  if (grid && 'MutationObserver' in window) {
    new MutationObserver(scan).observe(grid, { childList: true, subtree: true });
  }
})();
